import React, { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';

const toSlug = str => str.toLowerCase()
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const roleLabel = r => ({ super_admin: 'Super Admin', admin_general: 'Admin General', admin: 'Administrador', barbero: 'Barbero' }[r] || r);
const roleColor = r => ({ super_admin: 'cancelled', admin_general: 'rescheduled', admin: 'confirmed', barbero: 'attended' }[r] || 'pending');

const EMPTY_FORM = { name: '', email: '', password: '', role: 'admin', business_id: '', plan_id: '' };
const EMPTY_BIZ = { nombre: '', slug: '', ciudad: '', telefono: '', email: '' };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [businesses, setBusinesses] = useState([]);
  const [plans, setPlans] = useState([]);
  const [collapsed, setCollapsed] = useState({});
  const toggleCollapse = bizId => setCollapsed(prev => ({ ...prev, [bizId]: !prev[bizId] }));
  const [showModal, setShowModal] = useState(false);
  const [editUser, setEditUser] = useState(null); // null = crear, obj = editar
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modo de negocio: 'existing' | 'new'
  const [bizMode, setBizMode] = useState('existing');
  const [newBiz, setNewBiz] = useState(EMPTY_BIZ);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/users', { params: { search } });
      setUsers(res.data);
    } catch { setError('Error cargando usuarios'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/businesses').then(r => setBusinesses(r.data)).catch(() => {});
    api.get('/plans').then(r => setPlans(r.data)).catch(() => {});
  }, []);

  const filteredBusinesses = form.plan_id
    ? businesses.filter(b => String(b.plan_id) === String(form.plan_id))
    : businesses;

  const handleBizNameChange = val => {
    setNewBiz(b => ({ ...b, nombre: val, slug: b.slug ? b.slug : toSlug(val) }));
  };

  const openModal = () => {
    setEditUser(null);
    setForm(EMPTY_FORM);
    setNewBiz(EMPTY_BIZ);
    setBizMode('existing');
    setError('');
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditUser(u);
    setForm({
      name: u.name || '',
      email: u.email || '',
      password: '',
      role: u.role || 'admin',
      business_id: u.negocio_id ? String(u.negocio_id) : '',
      plan_id: u.plan_id ? String(u.plan_id) : '',
    });
    setError('');
    setShowModal(true);
  };

  const save = async () => {
    setSaving(true); setError('');
    try {
      if (editUser) {
        // Modo edición
        if (!form.name || !form.email) { setError('Nombre y correo son obligatorios'); setSaving(false); return; }
        // Si business_id está vacío en el form, preservar el original del usuario
        const businessId = form.business_id || editUser.negocio_id || null;
        await api.put(`/users/${editUser.id}`, {
          name: form.name,
          email: form.email,
          role: form.role,
          business_id: businessId,
        });
        // Actualizar plan solo si hay negocio válido y plan seleccionado
        const targetBiz = form.business_id || (editUser.negocio_id ? String(editUser.negocio_id) : null);
        if (targetBiz && targetBiz !== 'null' && form.plan_id) {
          await api.patch(`/businesses/${targetBiz}/plan`, { plan_id: form.plan_id });
        }
        setShowModal(false);
        load();
        setSuccess('Usuario actualizado');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        // Modo creación
        if (!form.name || !form.email || !form.password) { setError('Nombre, correo y contraseña son obligatorios'); setSaving(false); return; }
        let businessId = form.business_id;

        if (form.role !== 'super_admin') {
          if (!form.plan_id) { setError('Selecciona un plan'); setSaving(false); return; }

          if (bizMode === 'new') {
            if (!newBiz.nombre || !newBiz.slug) { setError('Nombre y link del negocio son obligatorios'); setSaving(false); return; }
            const bizRes = await api.post('/businesses', {
              name: newBiz.nombre, slug: newBiz.slug, city: newBiz.ciudad,
              phone: newBiz.telefono, email: newBiz.email || form.email,
              plan_id: form.plan_id, address: '',
            });
            businessId = bizRes.data.id;
          } else {
            if (!businessId) { setError('Selecciona un negocio'); setSaving(false); return; }
          }
        }

        await api.post('/users', { ...form, business_id: businessId });
        setShowModal(false);
        setForm(EMPTY_FORM);
        setNewBiz(EMPTY_BIZ);
        load();
        api.get('/businesses').then(r => setBusinesses(r.data));
        setSuccess('Usuario creado correctamente');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Error guardando usuario');
    } finally { setSaving(false); }
  };

  const toggleUser = async id => {
    try { await api.put(`/users/${id}/toggle`); load(); }
    catch { setError('Error actualizando usuario'); }
  };

  const resetPassword = async id => {
    const newPass = prompt('Nueva contraseña:');
    if (!newPass) return;
    try {
      await api.put(`/users/${id}/password`, { password: newPass });
      setSuccess('Contraseña actualizada');
      setTimeout(() => setSuccess(''), 3000);
    } catch { setError('Error actualizando contraseña'); }
  };

  const bookingOrigin = window.location.origin;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuarios</h1>
          <p className="page-subtitle">Gestion global de usuarios del sistema</p>
        </div>
        <button className="btn btn-primary" onClick={openModal}>+ Nuevo Usuario</button>
      </div>

      {success && <div className="alert alert-success" style={{ marginBottom: 16 }}>{success}</div>}
      {error && !showModal && <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="card">
        <div style={{ padding: '1rem 1.5rem', borderBottom: '1px solid var(--color-border)' }}>
          <input className="form-input" placeholder="Buscar por nombre o email..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>Cargando...</div>
        ) : (() => {
          // Agrupar usuarios por negocio
          const groups = {};
          users.forEach(u => {
            const bizId   = String(u.business_id || u.negocio_id || 'sin-negocio');
            const bizName = u.business_name || u.negocio_nombre || 'Sin negocio';
            const biz     = businesses.find(b => String(b.id) === bizId);
            if (!groups[bizId]) groups[bizId] = { bizId, bizName, biz, users: [] };
            groups[bizId].users.push(u);
          });

          return Object.values(groups).map(({ bizId, bizName, biz, users: groupUsers }) => {
            const isCollapsed = !!collapsed[bizId];
            return (
            <div key={bizId}>
              {/* Encabezado de grupo — clickeable para colapsar */}
              <div
                onClick={() => toggleCollapse(bizId)}
                style={{
                  padding: '10px 20px', background: 'var(--color-surface-2, #f8fafc)',
                  borderBottom: '1px solid var(--color-border)',
                  display: 'flex', alignItems: 'center', gap: 10,
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                <span style={{
                  fontSize: 13, color: 'var(--color-text-muted)',
                  transition: 'transform 0.2s',
                  display: 'inline-block',
                  transform: isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
                }}>▼</span>
                <span style={{ fontWeight: 700, fontSize: '0.92rem', color: 'var(--color-text)' }}>
                  🏪 {bizName}
                </span>
                {biz?.slug && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-gold)' }}>/book/{biz.slug}</span>
                )}
                {groupUsers[0]?.plan_name && (
                  <span className="badge badge-gold" style={{ marginLeft: 'auto' }}>{groupUsers[0].plan_name}</span>
                )}
                <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginLeft: biz?.slug ? 0 : 'auto' }}>
                  {groupUsers.length} usuario{groupUsers.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Tabla de usuarios del grupo — oculta si está colapsado */}
              {!isCollapsed && <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>Usuario</th><th>Rol</th><th>Estado</th><th>Acciones</th></tr>
                  </thead>
                  <tbody>
                    {groupUsers.map(u => (
                      <tr key={u.id}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{u.name || u.nombre}</div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{u.email}</div>
                        </td>
                        <td><span className={`badge badge-${roleColor(u.role || u.rol)}`}>{roleLabel(u.role || u.rol)}</span></td>
                        <td>
                          <span className={`badge badge-${(u.is_active || u.activo) ? 'attended' : 'cancelled'}`}>
                            {(u.is_active || u.activo) ? 'Activo' : 'Inactivo'}
                          </span>
                        </td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button className="btn btn-sm btn-primary" onClick={() => openEdit(u)}>Editar</button>
                            <button className="btn btn-sm btn-ghost" onClick={() => toggleUser(u.id)}>
                              {(u.is_active || u.activo) ? 'Desactivar' : 'Activar'}
                            </button>
                            <button className="btn btn-sm btn-ghost" onClick={() => resetPassword(u.id)}>Clave</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>}
            </div>
          );
          })
        })()}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{editUser ? 'Editar Usuario' : 'Nuevo Usuario'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
              {error && <div className="alert alert-error" style={{ marginBottom: 12 }}>{error}</div>}

              {/* Datos del usuario */}
              <div className="form-group">
                <label className="form-label">Nombre *</label>
                <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label className="form-label">Correo *</label>
                <input className="form-input" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              {!editUser && (
                <div className="form-group">
                  <label className="form-label">Contraseña *</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} />
                </div>
              )}
              <div className="form-group">
                <label className="form-label">Rol</label>
                <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value, business_id: '', plan_id: '' })}>
                  <option value="admin_general">Admin General</option>
                  <option value="admin">Administrador</option>
                  <option value="barbero">Barbero</option>
                </select>
              </div>

              {/* En edición: negocio + plan */}
              {editUser && form.role !== 'super_admin' && (
                <>
                  <div className="form-group">
                    <label className="form-label">Negocio</label>
                    <select className="form-select" value={form.business_id} onChange={e => {
                      const biz = businesses.find(b => String(b.id) === e.target.value);
                      setForm(f => ({ ...f, business_id: e.target.value, plan_id: String(biz?.plan_id || '') }));
                    }}>
                      <option value="">Sin negocio</option>
                      {businesses.map(b => <option key={b.id} value={String(b.id)}>{b.nombre}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Plan</label>
                    <select className="form-select" value={form.plan_id} onChange={e => setForm(f => ({ ...f, plan_id: e.target.value }))}>
                      <option value="">Sin plan</option>
                      {plans.map(p => <option key={p.id} value={String(p.id)}>{p.nombre_visible}</option>)}
                    </select>
                  </div>
                </>
              )}

              {!editUser && form.role !== 'super_admin' && (
                <>
                  {/* Plan */}
                  <div className="form-group">
                    <label className="form-label">Plan *</label>
                    <select className="form-select" value={form.plan_id} onChange={e => setForm({ ...form, plan_id: e.target.value, business_id: '' })}>
                      <option value="">Seleccionar plan...</option>
                      {plans.map(p => <option key={p.id} value={p.id}>{p.nombre_visible}</option>)}
                    </select>
                  </div>

                  {form.plan_id && (
                    <>
                      {/* Selector modo negocio */}
                      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                        <button
                          type="button"
                          className={`btn btn-sm ${bizMode === 'existing' ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ flex: 1 }}
                          onClick={() => setBizMode('existing')}
                        >
                          Negocio existente
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${bizMode === 'new' ? 'btn-primary' : 'btn-secondary'}`}
                          style={{ flex: 1 }}
                          onClick={() => { setBizMode('new'); setForm(f => ({ ...f, business_id: '' })); }}
                        >
                          + Crear nuevo negocio
                        </button>
                      </div>

                      {bizMode === 'existing' ? (
                        <div className="form-group">
                          <label className="form-label">Negocio *</label>
                          <select className="form-select" value={form.business_id} onChange={e => setForm({ ...form, business_id: e.target.value })}>
                            <option value="">Seleccionar negocio...</option>
                            {filteredBusinesses.map(b => (
                              <option key={b.id} value={b.id}>{b.nombre}</option>
                            ))}
                          </select>
                          {filteredBusinesses.length === 0 && (
                            <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: 6 }}>
                              No hay negocios con este plan. Usa "Crear nuevo negocio".
                            </p>
                          )}
                          {/* Mostrar link del negocio seleccionado */}
                          {form.business_id && (() => {
                            const biz = businesses.find(b => String(b.id) === String(form.business_id));
                            return biz?.slug ? (
                              <div style={{ marginTop: 8, padding: '10px 12px', background: 'var(--color-surface-raised)', borderRadius: 8, border: '1px solid var(--color-border)' }}>
                                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: 4 }}>Link de agendamiento de este negocio:</div>
                                <code style={{ fontSize: '0.82rem', color: 'var(--color-gold)', wordBreak: 'break-all' }}>
                                  {bookingOrigin}/book/{biz.slug}
                                </code>
                              </div>
                            ) : null;
                          })()}
                        </div>
                      ) : (
                        /* Formulario de nuevo negocio */
                        <div style={{ background: 'var(--color-surface-raised)', borderRadius: 10, padding: '16px', border: '1px solid var(--color-border)', marginBottom: 4 }}>
                          <div style={{ fontSize: '0.8rem', color: 'var(--color-gold)', fontWeight: 600, marginBottom: 14 }}>
                            Datos del nuevo negocio
                          </div>

                          <div className="form-group">
                            <label className="form-label">Nombre del negocio *</label>
                            <input
                              className="form-input"
                              placeholder="Ej: Barbería La 15"
                              value={newBiz.nombre}
                              onChange={e => handleBizNameChange(e.target.value)}
                            />
                          </div>

                          <div className="form-group">
                            <label className="form-label">
                              Link de agendamiento *
                              <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '0.75rem', marginLeft: 6 }}>
                                (unico por barberia)
                              </span>
                            </label>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }}>/book/</span>
                              <input
                                className="form-input"
                                placeholder="mi-barberia"
                                value={newBiz.slug}
                                onChange={e => setNewBiz(b => ({ ...b, slug: toSlug(e.target.value) }))}
                                style={{ flex: 1 }}
                              />
                            </div>
                            {newBiz.slug && (
                              <div style={{ marginTop: 6, fontSize: '0.78rem', background: 'var(--color-surface)', padding: '6px 10px', borderRadius: 6, wordBreak: 'break-all' }}>
                                <span style={{ color: 'var(--color-text-muted)' }}>Link: </span>
                                <span style={{ color: 'var(--color-gold)', fontWeight: 600 }}>
                                  {bookingOrigin}/book/{newBiz.slug}
                                </span>
                              </div>
                            )}
                          </div>

                          <div className="form-group">
                            <label className="form-label">Ciudad</label>
                            <input
                              className="form-input"
                              placeholder="Ej: Medellín"
                              value={newBiz.ciudad}
                              onChange={e => setNewBiz(b => ({ ...b, ciudad: e.target.value }))}
                            />
                          </div>

                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Teléfono del negocio</label>
                            <input
                              className="form-input"
                              placeholder="Ej: 3001234567"
                              value={newBiz.telefono}
                              onChange={e => setNewBiz(b => ({ ...b, telefono: e.target.value }))}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving ? 'Guardando...' : editUser ? 'Guardar Cambios' : 'Crear Usuario'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
